import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { Chart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/Chart';
import { SalesChartFilter } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/SalesChart/components/SalesChartFilter';
import { useSalesChartData } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-sales-chart-data';
import {
  EMPTY_Y_ZERO_VALUES,
  getEmptyXAxisValues,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/utils/empty-chart';
import type { GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { type Bar, BarChart } from '@/components/common/charts/BarChart';
import { Legend } from '@/components/common/charts/Legend';
import { Flex } from '@/components/common/Flex';
import { Spinner } from '@/components/common/Spinner';
import { Text } from '@/components/common/Text';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';
import { isDefined } from '@/utils/utils';

import { SalesChartOperationType } from '../../../../../../../../../../../generated/graphql';

import styles from './SalesChart.module.scss';

type SalesChartProps = WithClassName & {
  title: ReactNode;
  defaultCurrency: {
    id: string;
    name: string;
  };
  gateGroupDetails?: GateGroupDetails;
};

const EMPTY_X_VALUES = getEmptyXAxisValues();

export const SalesChart: FC<SalesChartProps> = (props) => {
  const t = useTranslations('pages.dashboard');

  const { className, title, defaultCurrency, gateGroupDetails } = props;

  const [filter, setFilter] = useState<SalesChartFilter | undefined>();

  const defaultFormValues = useMemo(() => {
    if (!gateGroupDetails) {
      return undefined;
    }

    return {
      currency: defaultCurrency,
    };
  }, [defaultCurrency, gateGroupDetails]);

  const {
    data,
    loading,
    refetch: refetchData,
  } = useSalesChartData({
    filter,
    gateGroupId: gateGroupDetails?.id,
  });

  const isEmptyState = useMemo(() => {
    const chart = data?.dashboardWidgetsSalesChartV2;

    if (!chart || !chart.data.length) {
      return true;
    }

    return !chart.data.some((entry) => (entry.invoice_amount ?? 0) > 0 || (entry.withdraw_amount ?? 0) > 0);
  }, [data?.dashboardWidgetsSalesChartV2]);

  const onFilter = useCallback<SubmitHandler<SalesChartFilter>>(
    (formData) => {
      setFilter(formData);

      // Если ничего не поменялось, аполло сам не перезапустит query
      if (isEqual(formData, filter)) {
        refetchData();
      }
    },
    [filter, refetchData]
  );

  const chartData = useMemo(() => {
    if (!data?.dashboardWidgetsSalesChartV2) {
      return {
        bars: [],
        xValues: [],
        yTicks: undefined,
      };
    }

    const bars: Bar[] = [];

    const addBar = (type: 'invoice' | 'withdraw', color: RegularColor) => {
      bars.push({
        id: type,
        name: t(`widgets.sales.labels.${type}`),
        values: data.dashboardWidgetsSalesChartV2.data.map((entry) => entry[`${type}_amount`]),
        color,
        categoryId: type,
      });
    };

    if (!filter || filter.operationType !== SalesChartOperationType.Withdraw) {
      addBar('invoice', 'green');
    }

    if (!filter || filter.operationType !== SalesChartOperationType.Invoice) {
      addBar('withdraw', 'yellow');
    }

    const xValues = data.dashboardWidgetsSalesChartV2.data.map(({ date }) => date);

    return {
      bars,
      xValues,
      yTicks: data.dashboardWidgetsSalesChartV2.y_axis_divisions?.length
        ? data.dashboardWidgetsSalesChartV2.y_axis_divisions
        : undefined,
    };
  }, [data, filter, t]);

  return (
    <Chart
      className={clsx(styles.root, className)}
      title={title}
      filter={
        <SalesChartFilter
          defaultValues={filter ?? defaultFormValues}
          isInGate={isDefined(gateGroupDetails)}
          onSubmit={onFilter}
        />
      }
      chart={
        loading ? (
          <Flex alignItems="center" justifyContent="center">
            <Spinner size={56} />
          </Flex>
        ) : isEmptyState ? (
          <Flex className={styles.emptyState} direction="column" justifyContent="spaceBetween">
            <div className={styles.emptyPlot}>
              <div className={styles.emptyPlotArea}>
                <div className={styles.emptyYAxis}>
                  {EMPTY_Y_ZERO_VALUES.map((value, index) => (
                    <Text key={`${value}-${index}`} className={styles.axisLabel} type="regular14" color="white-32p">
                      {value}
                    </Text>
                  ))}
                </div>

                <div className={styles.emptyGridLines}>
                  {EMPTY_Y_ZERO_VALUES.map((_, index) => (
                    <div key={index} className={styles.gridLine} />
                  ))}
                </div>
              </div>

              <div className={styles.emptyXAxis}>
                {EMPTY_X_VALUES.map((value) => (
                  <Text key={value} className={styles.axisLabel} type="regular14" color="white-32p">
                    {value}
                  </Text>
                ))}
              </div>
            </div>

            <Legend
              className={styles.emptyLegend}
              datasets={[
                {
                  id: 'invoice-empty',
                  name: t('widgets.sales.labels.invoice'),
                  color: 'white-32p',
                },
                {
                  id: 'withdraw-empty',
                  name: t('widgets.sales.labels.withdraw'),
                  color: 'white-32p',
                },
              ]}
              variant="borderOnly"
            />
          </Flex>
        ) : (
          <BarChart
            bars={chartData.bars}
            xValues={chartData.xValues}
            xAxisType="category"
            yTicks={chartData.yTicks}
            withLegend
          />
        )
      }
    />
  );
};
